/**
 * Allowlist + sanitization for user preferences that get mirrored into
 * telemetry sinks.
 *
 * `userPreferences` is a `Record<string, any>` that ANY feature can write to.
 * It is flattened into both the Datadog global context (UserContext) and the
 * per-event log properties (useLogger → App Insights + Datadog). Blindly
 * forwarding every entry is unsafe: a future preference could hold PII, a
 * token, or free-text user input, which would then be stamped on EVERY RUM
 * event + Log for the session in a third-party processor (Datadog EU).
 *
 * So we forward ONLY:
 *   1. keys on an explicit allowlist (add a key here only after confirming it
 *      can never carry personal data / secrets), and
 *   2. primitive scalar values (string/number/boolean) — never raw objects,
 *      arrays, or over-long free-text strings.
 */

import {
  isSensitiveTelemetryKey,
  redactSensitiveText,
  REDACTED_SENSITIVE_KEY,
} from './telemetryRedaction';

export const TELEMETRY_SAFE_PREFERENCE_KEYS: ReadonlySet<string> = new Set([
  'theme',
  'notifications',
  'fontSize',
  'language',
]);

export type TelemetryScalar = string | number | boolean;

// Free-text prefs shouldn't reach telemetry, but even allowlisted string values
// get a hard cap so a mis-set value can't bloat every event.
export const MAX_TELEMETRY_STRING_LENGTH = 200;

export const isSafeTelemetryPreference = (
  key: string,
  value: unknown,
): value is TelemetryScalar => {
  if (!TELEMETRY_SAFE_PREFERENCE_KEYS.has(key)) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (typeof value === 'string') return value.length <= MAX_TELEMETRY_STRING_LENGTH;
  return false;
};

/**
 * Returns the `[key, value]` pairs from `preferences` that are safe to send to
 * a telemetry sink. Shared by UserContext (Datadog global context) and
 * useLogger (App Insights + Datadog log properties) so both apply the SAME
 * allowlist and sanitization.
 */
export const safeTelemetryPreferenceEntries = (
  preferences: Record<string, unknown>,
): Array<[string, TelemetryScalar]> =>
  Object.entries(preferences).filter((entry): entry is [string, TelemetryScalar] =>
    isSafeTelemetryPreference(entry[0], entry[1]),
  );

// Hard cap on any string value forwarded via a log-property payload.
export const MAX_TELEMETRY_PROPERTY_STRING_LENGTH = 1000;

// Placeholder substituted for values we refuse to forward verbatim.
export const REDACTED_NON_SCALAR = '[redacted:non-scalar]';

/**
 * Sanitizes caller-supplied telemetry properties (`logger.event/error/warn/info`
 * `data`) before they are dual-written to App Insights + Datadog.
 *
 * Unlike `userPreferences` there is no key allowlist for ad-hoc log properties,
 * so we apply three defensive layers:
 *   - KEY deny-list: keys whose NAME signals auth/identity/secret material
 *     (email, token, authorization, loginHint, …) have their value dropped
 *     entirely regardless of content (see telemetryRedaction);
 *   - CONTENT redaction: string values are scrubbed of emails / JWTs / Bearer
 *     tokens, then length-capped;
 *   - SHAPE guard: objects / arrays / functions / symbols / bigint are replaced
 *     with a marker (the KEY — a developer-authored literal — is kept so the
 *     shape is visible, but the potentially PII/token/free-text VALUE is not
 *     forwarded to a third-party sink).
 *
 * Callers that need structured detail should log discrete scalar fields rather
 * than nesting an object.
 */
export const sanitizeTelemetryProperties = (
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!properties) return properties;

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (isSensitiveTelemetryKey(key)) {
      out[key] = REDACTED_SENSITIVE_KEY;
      continue;
    }
    if (value === null || value === undefined) {
      out[key] = value;
      continue;
    }
    const type = typeof value;
    if (type === 'boolean') {
      out[key] = value;
    } else if (type === 'number') {
      out[key] = Number.isFinite(value as number) ? value : String(value);
    } else if (type === 'string') {
      const s = redactSensitiveText(value as string);
      out[key] =
        s.length > MAX_TELEMETRY_PROPERTY_STRING_LENGTH
          ? `${s.slice(0, MAX_TELEMETRY_PROPERTY_STRING_LENGTH)}… [truncated]`
          : s;
    } else {
      out[key] = REDACTED_NON_SCALAR;
    }
  }
  return out;
};
